import React from 'react';

import { classnames, padding, useClassnames } from '@lumapps/classnames';
import { Alignment, Divider, FlexBox, Orientation, Size } from '@lumapps/lumx/react';
import { PendoTrigger } from '@lumapps/metric/components/PendoTrigger';
import { PENDO_SCOPES } from '@lumapps/metric/constants';
import { UserBlock } from '@lumapps/user-ui/components/UserBlock';
import { useCurrentUser } from '@lumapps/user/hooks/useCurrentUser';

import './index.scss';

export interface NavigationFooterProps {
    /** whether the footer is in loading state */
    isLoading?: boolean;
    /** tracking scope for user interactions */
    scope: string;
}

const CLASSNAME = 'backoffice-side-navigation-footer';

/**
 * Component displayed at bottom of the side navigation in the back office. It should not be reused
 *
 * @family Back office
 */
export const NavigationFooter: React.FC<NavigationFooterProps> = ({ isLoading = false, scope }) => {
    const { block } = useClassnames(CLASSNAME);
    const { user, isLoading: isUserLoading } = useCurrentUser();

    return (
        <li className={block({ 'is-sticky': true })}>
            <Divider />
            <FlexBox
                className={classnames([
                    padding('top', Size.big),
                    padding('bottom', Size.huge),
                    padding('horizontal', Size.big),
                ])}
                orientation={Orientation.vertical}
                vAlign={Alignment.left}
                gap={Size.tiny}
            >
                <PendoTrigger scope={PENDO_SCOPES.SIDE_NAV} showLabel />

                <UserBlock
                    className={padding('all', Size.regular)}
                    user={user}
                    fields={[]}
                    size={Size.s}
                    isLoading={isLoading || isUserLoading}
                    scope={scope}
                />
            </FlexBox>
        </li>
    );
};

/**
 * We set displayName to 'SideNavigationItem' to allow DS to render this component
 * inside @lumapps/lumx/react/SideNavigation. The DS validates children
 * and only renders components with this displayName.
 */
NavigationFooter.displayName = 'SideNavigationItem';
